/**
 * 浏览器指纹工具
 * 基于 FingerprintJS 生成唯一的设备标识
 */

import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { v4 as uuidv4 } from 'uuid'
import { getStorageKey } from './index'

// 使用统一的 storage key 生成方法
const STORAGE_KEY = getStorageKey('DEVICE_ID')

// FingerprintJS 实例（单例模式，延迟加载）
let fpPromise = null

/**
 * 初始化 FingerprintJS
 * 使用单例模式，确保只初始化一次
 * @returns {Promise} FingerprintJS 实例 Promise
 */
function initFingerprint() {
  if (!fpPromise) {
    fpPromise = FingerprintJS.load()
  }
  return fpPromise
}

/**
 * 生成设备指纹 ID（异步）
 * 优先从 localStorage 读取缓存的指纹，不存在则生成新指纹
 *
 * @returns {Promise<string>} 设备指纹 ID
 *
 * @example
 * // 在应用启动时预加载
 * import { getDeviceFingerprint } from '@/utils/fingerprint'
 * getDeviceFingerprint().then(id => {
 *   console.log('Device fingerprint:', id)
 * })
 */
export async function getDeviceFingerprint() {
  // 1. 尝试从 localStorage 读取缓存的指纹
  const cachedId = localStorage.getItem(STORAGE_KEY)
  if (cachedId) {
    return cachedId
  }

  try {
    // 2. 初始化 FingerprintJS 并生成指纹
    const fp = await initFingerprint()
    const result = await fp.get()
    const visitorId = result.visitorId

    // 3. 缓存到 localStorage
    localStorage.setItem(STORAGE_KEY, visitorId)

    return visitorId
  } catch (error) {
    // 4. 错误回退：如果指纹生成失败，使用 UUID 作为后备方案
    console.error('Failed to generate fingerprint, fallback to UUID:', error)

    const fallbackId = uuidv4()
    localStorage.setItem(STORAGE_KEY, fallbackId)

    return fallbackId
  }
}

/**
 * 同步获取设备 ID
 * 如果已缓存则返回缓存值，否则返回临时 UUID
 * 注意：此方法不会生成真正的浏览器指纹，建议在应用启动时先调用 getDeviceFingerprint()
 *
 * @returns {string} 设备 ID
 *
 * @example
 * // 在请求头中使用（需要同步获取）
 * import { getDeviceIdSync } from '@/utils/fingerprint'
 * const deviceId = getDeviceIdSync()
 */
export function getDeviceIdSync() {
  const cachedId = localStorage.getItem(STORAGE_KEY)
  if (cachedId) {
    return cachedId
  }

  // 如果没有缓存，返回临时 UUID
  // 建议在应用启动时先调用 getDeviceFingerprint() 进行异步初始化
  const tempId = uuidv4()
  localStorage.setItem(STORAGE_KEY, tempId)

  return tempId
}

/**
 * 重置设备指纹（用于测试或用户主动清除）
 * 清除 localStorage 中的缓存指纹和 FingerprintJS 实例
 */
export function resetDeviceFingerprint() {
  localStorage.removeItem(STORAGE_KEY)
  fpPromise = null
}

/**
 * 获取指纹详细信息（用于调试）
 * @returns {Promise<Object>} 指纹详细信息对象
 *
 * @example
 * import { getFingerprintDetails } from '@/utils/fingerprint'
 * getFingerprintDetails().then(details => {
 *   console.log('Fingerprint details:', details)
 * })
 */
export async function getFingerprintDetails() {
  try {
    const fp = await initFingerprint()
    const result = await fp.get()

    return {
      visitorId: result.visitorId,
      confidence: result.confidence,
      components: result.components,
    }
  } catch (error) {
    console.error('Failed to get fingerprint details:', error)
    return null
  }
}
